import {
  buildGoalLinkWrites,
  buildGoalParentLinkWrites,
} from '@griply/services';
import { Goal, ID } from '@griply/types';
import { getDirectGoalLifeAreaIds, getGoalParentIds } from '@griply/utils';
import { useCallback } from 'react';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';

import { useUpdateGoal } from './use-update-goal';

export type MoveGoalTarget = {
  parentIds: ID[];
  lifeAreaIds: ID[];
  archive?: boolean;
};

// Both link types live on one goal document, so their updates merge into a single write.
export const useMoveGoal = () => {
  const { submit: updateGoal } = useUpdateGoal();
  const updateRelationships = useUpdateRelationshipsReferences();
  const links = useEntityLinks();

  return useCallback(
    (goal: Goal, { parentIds, lifeAreaIds, archive }: MoveGoalTarget) => {
      const parentWrites = buildGoalParentLinkWrites({
        goalId: goal.id,
        parentIds,
        currentParentIds: getGoalParentIds(goal, { links }),
        isMigrated: !!goal.parentLinksMigrated,
      });
      const linkWrites = buildGoalLinkWrites({
        goalId: goal.id,
        lifeAreaIds,
        currentLifeAreaIds: getDirectGoalLifeAreaIds(goal, links),
        isMigrated: !!goal.lifeAreaLinksMigrated,
      });

      const goalUpdate = {
        ...(parentWrites.goalUpdate ?? {}),
        ...(linkWrites.goalUpdate ?? {}),
        ...(archive ? { archivedAt: new Date() } : {}),
      };

      if (Object.keys(goalUpdate).length) {
        updateGoal({ ...goalUpdate, id: goal.id }, true);
      }

      const relationshipUpdates = [
        ...parentWrites.relationshipUpdates,
        ...linkWrites.relationshipUpdates,
      ];

      if (relationshipUpdates.length) {
        updateRelationships(relationshipUpdates);
      }
    },
    [links, updateGoal, updateRelationships],
  );
};
